import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Send, AlertTriangle, Info, RefreshCw } from "lucide-react";

/**
 * Phase X1 — Feb 2026: Send-to-Xero panel injected into the payout
 * run detail drawer. Only rendered for `paid` runs.
 *
 * Props:
 *   • reference   — payout_reference (e.g. PWS-PAYOUT-2026-02-007)
 *   • runStatus   — current run.status ("paid" required to enable)
 *   • uploadState — the `xero_upload` sub-doc from the run doc (may be null)
 *   • onMutated   — callback after a successful send so the parent can reload
 */
export default function XeroUploadSection({
  reference, runStatus, uploadState, onMutated,
}) {
  const [settings, setSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLive, setConfirmLive] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoadingSettings(true);
    try {
      const { data } = await api.get("/admin/settings/xero-upload");
      setSettings(data);
    } catch {
      setSettings(null);
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const canSend = useMemo(() => {
    if (runStatus !== "paid") return false;
    if (!settings) return false;
    if (!settings.enabled) return false;
    if (!settings.xero_upload_email) return false;
    return true;
  }, [runStatus, settings]);

  const performSend = async () => {
    setSending(true);
    try {
      const { data } = await api.post(
        `/admin/payout-runs/${encodeURIComponent(reference)}/xero-upload`,
        { confirm_live: !!confirmLive },
      );
      if (data.delivery_label === "emailed_to_xero_inbox") {
        toast.success(`Emailed to Xero inbox (${data.recipient})`);
      } else if (data.delivery_label === "emailed_to_preview_inbox") {
        toast.message(`Emailed to TEST/preview inbox — NOT Xero (preview environment only)`);
      } else if (data.delivery_label === "blocked_production_reroute") {
        toast.error(`BLOCKED — would have gone to a test inbox in production. ${data.error || ""}`);
      } else {
        toast.error(`Xero send: ${data.status}${data.error ? ` — ${data.error}` : ""}`);
      }
      setConfirmOpen(false);
      setConfirmLive(false);
      onMutated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  // Status pill — tri-state, never ambiguous between a real Xero
  // delivery and a preview/test reroute or a blocked production send.
  const deliveryLabel = uploadState?.delivery_label
    || (uploadState?.status === "sent"
          ? (uploadState?.preview_override ? "emailed_to_preview_inbox" : "emailed_to_xero_inbox")
          : uploadState?.status === "failed" ? "send_failed"
          : uploadState?.status === "skipped_no_key" ? "send_skipped_no_resend_key"
          : "not_sent");
  let pillClass = "bg-slate-100 text-slate-600 border-slate-300";
  let pillLabel = "Not sent to Xero";
  if (deliveryLabel === "emailed_to_xero_inbox") {
    pillClass = "bg-emerald-100 text-emerald-800 border-emerald-300";
    pillLabel = "Emailed to Xero inbox";
  } else if (deliveryLabel === "emailed_to_preview_inbox") {
    pillClass = "bg-indigo-100 text-indigo-800 border-indigo-300";
    pillLabel = "Emailed to test/preview inbox (NOT Xero)";
  } else if (deliveryLabel === "blocked_production_reroute") {
    pillClass = "bg-rose-100 text-rose-800 border-rose-300 font-bold";
    pillLabel = "BLOCKED — would have gone to test inbox";
  } else if (deliveryLabel === "send_failed") {
    pillClass = "bg-rose-100 text-rose-800 border-rose-300";
    pillLabel = "Send failed";
  } else if (deliveryLabel === "send_skipped_no_resend_key") {
    pillClass = "bg-amber-100 text-amber-800 border-amber-300";
    pillLabel = "Not sent — Resend key missing";
  }

  return (
    <div
      data-testid="pws-payout-xero-panel"
      className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4"
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <Send className="w-4 h-4 text-indigo-600" /> Xero Upload
        </div>
        <span
          data-testid="pws-payout-xero-status-chip"
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${pillClass}`}
        >
          {pillLabel}
        </span>
        {deliveryLabel === "blocked_production_reroute" && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-rose-600 text-white border border-rose-700">
            <AlertTriangle className="w-3 h-3" /> production misconfiguration
          </span>
        )}
      </div>

      {deliveryLabel === "blocked_production_reroute" && (
        <div
          data-testid="pws-payout-xero-blocked-banner"
          className="mb-3 text-xs text-rose-900 bg-rose-50 border border-rose-300 rounded-lg p-2.5 flex items-start gap-2"
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-rose-600" />
          <div>
            <div className="font-bold">This email never reached Xero.</div>
            It was rerouted to a test/preview inbox despite running in
            production. Check <code className="bg-white/70 rounded px-1">TEST_EMAIL_RECIPIENT</code>{" "}
            / <code className="bg-white/70 rounded px-1">FORCE_TEST_EMAIL_OVERRIDE</code>{" "}
            / <code className="bg-white/70 rounded px-1">XERO_UPLOAD_PREVIEW_TO</code> in the
            production environment and clear them before resending.
          </div>
        </div>
      )}

      {uploadState && (
        <div className="text-[11px] text-slate-600 space-y-0.5 mb-3 font-mono">
          <div><span className="font-sans font-semibold text-slate-500">Sent at:</span> {uploadState.sent_at || "—"}</div>
          <div><span className="font-sans font-semibold text-slate-500">Sent by:</span> {uploadState.sent_by || "—"}</div>
          <div><span className="font-sans font-semibold text-slate-500">Recipient:</span> {uploadState.recipient || "—"}</div>
          <div><span className="font-sans font-semibold text-slate-500">Attempts:</span> {uploadState.attempts || 0}</div>
          {uploadState.last_error && (
            <div className="text-rose-700"><span className="font-sans font-semibold">Last error:</span> {uploadState.last_error}</div>
          )}
        </div>
      )}

      {loadingSettings ? (
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <RefreshCw className="w-3 h-3 animate-spin" /> Loading Xero settings…
        </div>
      ) : !settings?.enabled || !settings?.xero_upload_email ? (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            Xero upload is not configured. Set the Xero upload email in{" "}
            <a href="/admin/settings/xero-upload" className="underline font-semibold">
              /admin/settings → Xero Upload
            </a>{" "}
            before sending.
          </div>
        </div>
      ) : runStatus !== "paid" ? (
        <div className="text-xs text-slate-500 flex items-start gap-2">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Only <span className="font-semibold">paid</span> payout runs can be sent to Xero.
        </div>
      ) : !confirmOpen ? (
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={!canSend || sending}
          data-testid="pws-payout-xero-send-btn"
          className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"
          size="sm"
        >
          <Send className="w-3.5 h-3.5 mr-1.5" />
          {uploadState?.status === "sent" ? "Resend to Xero" : "Send to Xero"}
        </Button>
      ) : (
        <div
          data-testid="pws-payout-xero-confirm-panel"
          className="rounded-lg border border-indigo-300 bg-white p-3 space-y-2"
        >
          <div className="text-xs text-slate-800">
            <div><span className="font-semibold">Recipient:</span> <span className="font-mono">{settings.xero_upload_email}</span></div>
            <div><span className="font-semibold">Payout:</span> {reference}</div>
            <div><span className="font-semibold">Attachment:</span> SCWT-Partner-Payout-Statement-{reference}.pdf</div>
          </div>
          <label className="flex items-start gap-2 text-[11px] text-slate-700">
            <input
              type="checkbox"
              checked={confirmLive}
              onChange={(e) => setConfirmLive(e.target.checked)}
              data-testid="pws-payout-xero-confirm-live"
              className="mt-0.5"
            />
            <span>
              Confirm live send to the real Xero inbox <span className="text-slate-500">(ignored in preview — sends will still route to the preview inbox)</span>
            </span>
          </label>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => { setConfirmOpen(false); setConfirmLive(false); }}
              disabled={sending}
              className="rounded-full"
            >
              <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
            <Button
              onClick={performSend} disabled={sending}
              data-testid="pws-payout-xero-confirm-send-btn"
              className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"
              size="sm"
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
              {sending ? "Sending…" : "Send now"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
